import React, {
    useEffect, useCallback, useState, useContext,
    useMemo,
} from 'react';
import {
    gql, useQuery, useMutation,
} from '@apollo/client';
import { Portal } from 'react-portal';
import { Transition } from 'react-transition-group';
import cn from 'classnames';

import { STORE_USER_DATA } from 'app/containers/DataSaverUser/storeUserDataMutation';

import { AuthContext } from 'app/containers/AuthContainer';
import { errorService } from 'global/services';
import { OfferPopup } from './OfferPopup';

import css from './offerGuard.module.scss';

const container = document.querySelector('#root') || document.body;

const lawPages = ['/offer/', '/privacy_policy/', '/personal_data_policy/', '/cookie_policy/'];

export function OfferOpener({ isScalable, realm }: any) {
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [phone, setPhone] = useState(sessionStorage.getItem('phone'));

    const { isAuthed } = useContext(AuthContext);

    const [storeUserData] = useMutation(STORE_USER_DATA, {
        onError: (error) => {
            // @ts-ignore
            errorService.log(error);
        },
        context: {
            message: 'root:mutate:DataSaverUser',
        },
    });

    const { data } = useQuery(gql`
        query USER_AGREEMENT_STATUS_QUERY {
                user {
                    id
                    isAgreementAccepted
                }
        }`,
    {
        fetchPolicy: 'network-only',
    },
    );

    const { data: guestData } = useQuery(gql`
        query GUEST_AGREEMENT_STATUS_QUERY {
                getEnvData {
                    isAgreementAccepted
                }
        }`,
    {
        fetchPolicy: 'network-only',
    },
    );

    const isAgreementAccepted = useMemo(
        () => (isAuthed
            ? data?.user?.isAgreementAccepted
            : guestData?.getEnvData?.isAgreementAccepted),
        [data, guestData, isAuthed],
    );

    const togglePopup = useCallback(() => {
        setIsPopupOpen((isOpen) => !isOpen);
    }, []);

    const saveSeenState = useCallback(
        () => {
            storeUserData({
                variables:
                {
                    isAgreementAccepted: true,
                },
            });
        },
        [storeUserData],
    );

    const handlePhoneChange = useCallback(
        ({ detail: { phone: newPhone } }: any): void => {
            setPhone(newPhone);
        },
        [],
    );

    const isLawPages = useMemo(
        () => lawPages.some((page) => page === window.location?.pathname),
        [],
    );

    useEffect(() => {
        container.classList.add(css.offerBodyAnimation);
        return () => {
            container.classList.remove(css.offerBodyAnimation);
            container.classList.remove(css.offerBodyAnimationOpen);
        };
    }, []);

    useEffect(() => {
        if (isPopupOpen) {
            container.classList.add(css.offerBodyAnimationOpen);
        } else {
            container.classList.remove(css.offerBodyAnimationOpen);
        }
    }, [isPopupOpen]);

    useEffect(
        () => {
            if (isAgreementAccepted === false && !isLawPages) {
                if (isAuthed || (realm === 'promo-checkout' && phone)) {
                    togglePopup();
                }
            }
        },
        [
            togglePopup,
            isAuthed, isLawPages, isAgreementAccepted,
            realm,
            phone,
        ],
    );

    useEffect(
        () => {
            window.addEventListener('changePhone', handlePhoneChange);

            return () => window.removeEventListener('changePhone', handlePhoneChange);
        },
        [handlePhoneChange],
    );

    return (
        <Portal>
            <Transition
                in={isPopupOpen}
                timeout={500}
            >
                {(transitionState) => {
                    const containerClasses = cn({
                        [css.offerPopupTransition]: true,
                        [css[transitionState]]: true,
                    });

                    const backdropClasses = cn({
                        [css.offerPopupBackdrop]: true,
                        [css[transitionState]]: true,
                    });

                    return (
                        <div
                            className={css.offerPopupWrapRoot}
                        >
                            <div className={backdropClasses} />
                            <div className={containerClasses}>
                                <OfferPopup
                                    onAccept={() => {
                                        saveSeenState();
                                        togglePopup();
                                    }}
                                    isAuthed={isAuthed}
                                    isScalable={isScalable}
                                />
                            </div>
                        </div>
                    );
                }}
            </Transition>
        </Portal>
    );
}
